import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  createWorkOrder, 
  getUsers, 
  getEquipmentTypes, 
  getStations, 
  createStation,
  getDropdownOptions,
  createLogisticsRequest,
  generateTagBasedSafety
} from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Save, MapPin, Plus, X, ChevronsUpDown, Check, Users, Truck, Star, AlertTriangle, Shield, Loader2, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const CreateWorkOrderPage = () => {
  const navigate = useNavigate();
  const { isSupervisor } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [stations, setStations] = useState([]);
  const [showAddStation, setShowAddStation] = useState(false);
  const [newStationName, setNewStationName] = useState('');
  
  // Dropdown options from admin
  const [scopeOptions, setScopeOptions] = useState([]);
  const [assetTypeOptions, setAssetTypeOptions] = useState([]);
  const [riskFactorOptions, setRiskFactorOptions] = useState([]);
  
  // Safety guidance state
  const [safetyGuidance, setSafetyGuidance] = useState(null);
  const [generatingSafety, setGeneratingSafety] = useState(false);
  
  const [formData, setFormData] = useState({
    wo_number: '',
    title: '',
    description: '',
    scope: '',
    scope_tags: [], // Multi-select scope options
    location_name: '',
    location_lat: null,
    location_lng: null,
    stations: [],
    bays: '',
    start_date: '',
    end_date: '',
    project_type: '',
    asset_type: '',
    asset_bay_location: '',
    risk_factors: [], // Multi-select risk factors
    equipment_type: '',
    outage_window: '',
    tags: '',
    crew_leader_id: '',
    safety_notes: '',
    equipment_requested: '',
    order_placed: '',
    tce_needed: '',
    crew_size: {},
    // Equipment Request Options
    star_team_required: 'no',
    star_team_notes: '',
    heavy_hauling_required: 'no',
    heavy_hauling_details: {
      size: '',
      weight: '',
      pickup_location: '',
      dropoff_location: ''
    }
  });

  const [crewClasses] = useState([
    { id: 'crew_leader', label: 'Crew Leader' },
    { id: 'a_mechanic', label: 'A Mechanic' },
    { id: 'b_mechanic', label: 'B Mechanic' },
    { id: 'helper', label: 'Helper' }
  ]);

  useEffect(() => {
    if (!isSupervisor()) {
      toast.error('You do not have permission to create work orders');
      navigate('/work-orders');
      return;
    }
    fetchUsers();
    fetchEquipmentTypes();
    fetchStations();
    fetchDropdownOptions();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchEquipmentTypes = async () => {
    try {
      const response = await getEquipmentTypes();
      setEquipmentTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment types:', error);
    }
  };

  const fetchStations = async () => {
    try {
      const response = await getStations();
      setStations(response.data);
    } catch (error) {
      console.error('Failed to fetch stations:', error);
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      const [scopeRes, assetRes, riskRes] = await Promise.all([
        getDropdownOptions('scope'),
        getDropdownOptions('asset_type'),
        getDropdownOptions('risk_factor')
      ]);
      setScopeOptions(scopeRes.data || []);
      setAssetTypeOptions(assetRes.data || []);
      setRiskFactorOptions(riskRes.data || []);
    } catch (error) {
      console.error('Failed to fetch dropdown options:', error);
      // Use fallback options if API fails
      setScopeOptions([
        { id: '1', value: 'transformer_work', label: 'Transformer Work' },
        { id: '2', value: 'breaker_maintenance', label: 'Breaker Maintenance' },
        { id: '3', value: 'relay_testing', label: 'Relay Testing' },
        { id: '4', value: 'switching_operations', label: 'Switching Operations' },
        { id: '5', value: 'cable_installation', label: 'Cable Installation' }
      ]);
      setAssetTypeOptions([
        { id: '1', value: 'transformer', label: 'Transformer' },
        { id: '2', value: 'circuit_breaker', label: 'Circuit Breaker' },
        { id: '3', value: 'disconnect_switch', label: 'Disconnect Switch' },
        { id: '4', value: 'protection_relay', label: 'Protection Relay' }
      ]);
      setRiskFactorOptions([
        { id: '1', value: 'arc_flash', label: 'Arc Flash Hazard' },
        { id: '2', value: 'high_voltage', label: 'High Voltage' },
        { id: '3', value: 'confined_space', label: 'Confined Space' },
        { id: '4', value: 'elevated_work', label: 'Elevated Work / Fall Risk' },
        { id: '5', value: 'heavy_lifting', label: 'Heavy Lifting' }
      ]);
    }
  };

  // Generate safety guidance when asset type or risk factors change
  const handleGenerateSafetyGuidance = async () => {
    if (!formData.asset_type && formData.risk_factors.length === 0) {
      toast.error('Please select an Asset Type or Risk Factors first');
      return;
    }
    
    setGeneratingSafety(true);
    try {
      // Create a temporary work order ID for the guidance
      const tempId = 'temp-' + Date.now();
      const tags = [
        formData.asset_type,
        ...formData.risk_factors,
        ...formData.scope_tags,
        formData.equipment_type
      ].filter(Boolean);
      
      const context = `
        Location: ${formData.location_name || 'Not specified'}
        Project Type: ${formData.project_type || 'Not specified'}
        Equipment: ${formData.equipment_type || 'Not specified'}
        Description: ${formData.description || 'Not specified'}
      `;
      
      // Note: For new work orders, we'll generate guidance after creation
      // For now, show a preview based on selected tags
      const assetLabel = assetTypeOptions.find(o => o.value === formData.asset_type)?.label || formData.asset_type;
      const riskLabels = formData.risk_factors.map(rf => 
        riskFactorOptions.find(o => o.value === rf)?.label || rf
      );
      
      setSafetyGuidance({
        preview: true,
        asset_type: assetLabel,
        risk_factors: riskLabels,
        message: `Safety guidance will be auto-generated for: ${assetLabel || 'Selected Asset'} with risk factors: ${riskLabels.join(', ') || 'None selected'}. Full AskStewie analysis will be available after work order creation.`
      });
      
      toast.success('Safety guidance preview generated');
    } catch (error) {
      console.error('Failed to generate safety guidance:', error);
      toast.error('Failed to generate safety guidance');
    } finally {
      setGeneratingSafety(false);
    }
  };

  const handleAddStation = async () => {
    if (!newStationName.trim()) return;
    try {
      const response = await createStation(newStationName.trim());
      setStations([...stations, response.data]);
      // Add to selected stations
      setFormData(prev => ({
        ...prev,
        stations: [...prev.stations, newStationName.trim()]
      }));
      setNewStationName('');
      setShowAddStation(false);
      toast.success('Station added');
    } catch (error) {
      toast.error('Failed to add station');
    }
  };

  const handleStationSelect = (stationName) => {
    if (formData.stations.includes(stationName)) {
      setFormData(prev => ({
        ...prev,
        stations: prev.stations.filter(s => s !== stationName)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        stations: [...prev.stations, stationName]
      }));
    }
  };

  const handleRemoveStation = (stationName) => {
    setFormData(prev => ({
      ...prev,
      stations: prev.stations.filter(s => s !== stationName)
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.wo_number || !formData.title || !formData.location_name) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        stations: formData.stations,
        bays: formData.bays ? formData.bays.split(',').map(s => s.trim()) : [],
        tags: formData.tags ? formData.tags.split(',').map(s => s.trim()) : [],
        assigned_crew: [],
        scope_tags: formData.scope_tags,
        risk_factors: formData.risk_factors
      };

      const response = await createWorkOrder(payload);
      const workOrderId = response.data.id;
      
      // Create logistics requests if equipment options selected
      if (formData.star_team_required === 'yes') {
        try {
          await createLogisticsRequest({
            work_order_id: workOrderId,
            request_type: 'star_team',
            notes: formData.star_team_notes,
            details: { notes: formData.star_team_notes }
          });
          toast.success('Star Team request created');
        } catch (err) {
          console.error('Failed to create Star Team request:', err);
        }
      }
      
      if (formData.heavy_hauling_required === 'yes') {
        try {
          await createLogisticsRequest({
            work_order_id: workOrderId,
            request_type: 'heavy_hauling',
            notes: `Size: ${formData.heavy_hauling_details.size}, Weight: ${formData.heavy_hauling_details.weight}`,
            details: formData.heavy_hauling_details
          });
          toast.success('Heavy Hauling request created');
        } catch (err) {
          console.error('Failed to create Heavy Hauling request:', err);
        }
      }
      
      // Generate safety guidance if asset type or risk factors selected
      if (formData.asset_type || formData.risk_factors.length > 0) {
        try {
          const tags = [
            formData.asset_type,
            ...formData.risk_factors,
            ...formData.scope_tags,
            formData.equipment_type
          ].filter(Boolean);
          
          await generateTagBasedSafety(workOrderId, tags, ["SAFETY", "UNION"], formData.description);
          toast.success('Safety guidance generated');
        } catch (err) {
          console.error('Failed to generate safety guidance:', err);
        }
      }
      
      toast.success('Work Order created successfully');
      navigate(`/work-orders/${workOrderId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create work order');
    } finally {
      setLoading(false);
    }
  };

  const crewLeaders = users.filter(u => ['admin', 'supervisor', 'crew_leader'].includes(u.role));

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="create-work-order-page">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate('/work-orders')} className="mb-2 -ml-2">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Work Orders
        </Button>
        <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Create Work Order
        </h1>
        <p className="text-slate-600">Fill in the details to create a new project</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="wo_number">Work Order Number *</Label>
                  <Input
                    id="wo_number"
                    placeholder="WO-2025-XXX"
                    value={formData.wo_number}
                    onChange={(e) => handleChange('wo_number', e.target.value)}
                    required
                    data-testid="wo-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project_type">Project Type</Label>
                  <Select value={formData.project_type} onValueChange={(v) => handleChange('project_type', v)}>
                    <SelectTrigger data-testid="project-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Equipment Replacement">Equipment Replacement</SelectItem>
                      <SelectItem value="Testing">Testing</SelectItem>
                      <SelectItem value="Emergency">Emergency</SelectItem>
                      <SelectItem value="Maintenance">Maintenance</SelectItem>
                      <SelectItem value="New Installation">New Installation</SelectItem>
                      <SelectItem value="Inspection">Inspection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Transformer Replacement - Oak Street Substation"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                  data-testid="title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Detailed description of the work to be performed..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  data-testid="description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="scope">Scope Summary</Label>
                <Input
                  id="scope"
                  placeholder="Major equipment replacement, preventive maintenance, etc."
                  value={formData.scope}
                  onChange={(e) => handleChange('scope', e.target.value)}
                  data-testid="scope-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Scope of Work - Multi-Select */}
          <Card className="border-blue-200 bg-gradient-to-br from-slate-50 to-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <ClipboardList className="w-5 h-5" />
                Scope of Work
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Select all applicable scope items for this work order. These selections help auto-generate safety guidance.
              </p>
              
              {/* Scope Tags Multi-Select */}
              <div className="space-y-2">
                <Label>Scope Tags</Label>
                {formData.scope_tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.scope_tags.map((tag) => {
                      const option = scopeOptions.find(o => o.value === tag);
                      return (
                        <Badge key={tag} variant="secondary" className="px-3 py-1.5 bg-blue-100 text-blue-800 flex items-center gap-2">
                          <span>{option?.label || tag}</span>
                          <button 
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              scope_tags: prev.scope_tags.filter(t => t !== tag)
                            }))}
                            className="hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between"
                    >
                      {formData.scope_tags.length > 0 
                        ? `${formData.scope_tags.length} scope item(s) selected`
                        : "Select scope items..."
                      }
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <div className="max-h-60 overflow-auto">
                      {scopeOptions.length === 0 ? (
                        <div className="p-4 text-sm text-slate-500 text-center">
                          No scope options available. Admin can add them in settings.
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {scopeOptions.map((option) => {
                            const isSelected = formData.scope_tags.includes(option.value);
                            return (
                              <div 
                                key={option.id || option.value}
                                className={`flex items-center space-x-3 p-2 rounded-md cursor-pointer hover:bg-slate-100 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                                onClick={() => {
                                  if (isSelected) {
                                    setFormData(prev => ({
                                      ...prev,
                                      scope_tags: prev.scope_tags.filter(t => t !== option.value)
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      scope_tags: [...prev.scope_tags, option.value]
                                    }));
                                  }
                                }}
                              >
                                <Checkbox 
                                  checked={isSelected}
                                  className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                                />
                                <span className="flex-1 text-sm">{option.label}</span>
                                {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500">Click to select multiple scope items</p>
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="location_name">Location Name *</Label>
                <Input
                  id="location_name"
                  placeholder="Oak Street Substation"
                  value={formData.location_name}
                  onChange={(e) => handleChange('location_name', e.target.value)}
                  required
                  data-testid="location-input"
                />
              </div>

              {/* Station Selection - Multi-Select */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Stations</Label>
                  <Dialog open={showAddStation} onOpenChange={setShowAddStation}>
                    <DialogTrigger asChild>
                      <Button type="button" variant="outline" size="sm" data-testid="add-station-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Add New Station
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-gradient-to-br from-slate-50 via-green-50 to-emerald-50 border-green-200">
                      <DialogHeader>
                        <DialogTitle className="text-green-800">Add New Station</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label className="text-green-800">Station Name/Number</Label>
                          <Input
                            placeholder="e.g., Station 42, Oak Street Sub"
                            value={newStationName}
                            onChange={(e) => setNewStationName(e.target.value)}
                            className="bg-white/80 border-green-200 focus:border-green-400"
                            data-testid="new-station-input"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => setShowAddStation(false)} className="border-green-300 hover:bg-green-100">
                            Cancel
                          </Button>
                          <Button type="button" onClick={handleAddStation} className="bg-green-600 hover:bg-green-700" data-testid="save-station-btn">
                            Add Station
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                {/* Selected Stations - Show badges above dropdown */}
                {formData.stations.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.stations.map((station, i) => (
                      <Badge key={i} variant="secondary" className="px-3 py-1.5 flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200">
                        {station}
                        <button 
                          type="button"
                          onClick={() => handleRemoveStation(station)}
                          className="ml-1 hover:text-red-500 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Station Multi-Select Dropdown using Popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between font-normal"
                      data-testid="station-select"
                    >
                      {formData.stations.length === 0 
                        ? "Select stations..." 
                        : `${formData.stations.length} station${formData.stations.length > 1 ? 's' : ''} selected`}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <div className="max-h-[300px] overflow-y-auto">
                      {stations.length === 0 ? (
                        <div className="p-4 text-sm text-slate-500 text-center">
                          No stations yet. Click &quot;Add New Station&quot; to create one.
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {stations.map((station) => {
                            const isSelected = formData.stations.includes(station.name);
                            return (
                              <div 
                                key={station.id || station.name}
                                className={`flex items-center space-x-3 p-2 rounded-md cursor-pointer hover:bg-slate-100 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                                onClick={() => handleStationSelect(station.name)}
                              >
                                <Checkbox 
                                  id={`station-${station.id || station.name}`}
                                  checked={isSelected}
                                  onClick={(e) => e.stopPropagation()}
                                  onCheckedChange={() => handleStationSelect(station.name)}
                                  className="data-[state=checked]:bg-[#0056D2] data-[state=checked]:border-[#0056D2]"
                                />
                                <span className="flex-1 text-sm">
                                  {station.name}
                                </span>
                                {isSelected && <Check className="w-4 h-4 text-[#0056D2]" />}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500">Click to select one or more stations for this work order</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bays">Bays (comma separated)</Label>
                <Input
                  id="bays"
                  placeholder="Bay 1, Bay 2, Bay 3"
                  value={formData.bays}
                  onChange={(e) => handleChange('bays', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location_lat">Latitude</Label>
                  <Input
                    id="location_lat"
                    type="number"
                    step="any"
                    placeholder="40.7589"
                    value={formData.location_lat || ''}
                    onChange={(e) => handleChange('location_lat', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location_lng">Longitude</Label>
                  <Input
                    id="location_lng"
                    type="number"
                    step="any"
                    placeholder="-73.9851"
                    value={formData.location_lng || ''}
                    onChange={(e) => handleChange('location_lng', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card>
            <CardHeader>
              <CardTitle>Schedule & Assignment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    data-testid="end-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="crew_leader_id">Crew Leader</Label>
                  <Select value={formData.crew_leader_id} onValueChange={(v) => handleChange('crew_leader_id', v)}>
                    <SelectTrigger data-testid="crew-leader-select">
                      <SelectValue placeholder="Select leader" />
                    </SelectTrigger>
                    <SelectContent>
                      {crewLeaders.map((user) => (
                        <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="outage_window">Outage Window</Label>
                <Input
                  id="outage_window"
                  placeholder="2025-01-20 to 2025-01-25"
                  value={formData.outage_window}
                  onChange={(e) => handleChange('outage_window', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Asset & Safety Intelligence */}
          <Card className="border-amber-200 bg-gradient-to-br from-slate-50 to-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Shield className="w-5 h-5" />
                Asset & Safety Intelligence
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Select Asset Type and Risk Factors to auto-generate AskStewie safety guidance.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="equipment_type">Equipment Type</Label>
                  <Select value={formData.equipment_type} onValueChange={(v) => handleChange('equipment_type', v)}>
                    <SelectTrigger data-testid="equipment-type-select">
                      <SelectValue placeholder="Select equipment" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipmentTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="asset_type">Asset Type</Label>
                  <Select value={formData.asset_type} onValueChange={(v) => handleChange('asset_type', v)}>
                    <SelectTrigger data-testid="asset-type-select">
                      <SelectValue placeholder="Select asset type" />
                    </SelectTrigger>
                    <SelectContent>
                      {assetTypeOptions.length > 0 ? (
                        assetTypeOptions.map((option) => (
                          <SelectItem key={option.id || option.value} value={option.value}>{option.label}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="transformer">Transformer</SelectItem>
                          <SelectItem value="circuit_breaker">Circuit Breaker</SelectItem>
                          <SelectItem value="disconnect_switch">Disconnect Switch</SelectItem>
                          <SelectItem value="protection_relay">Protection Relays</SelectItem>
                          <SelectItem value="bus_work">Bus Work</SelectItem>
                          <SelectItem value="control_building">Control Building</SelectItem>
                          <SelectItem value="battery_system">Battery System</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="asset_bay_location">Asset Bay/Location</Label>
                <Input
                  id="asset_bay_location"
                  placeholder="Bay 1A, Position 3, etc."
                  value={formData.asset_bay_location}
                  onChange={(e) => handleChange('asset_bay_location', e.target.value)}
                />
              </div>

              {/* Risk Factors Multi-Select */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Risk Factors
                </Label>
                {formData.risk_factors.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.risk_factors.map((factor) => {
                      const option = riskFactorOptions.find(o => o.value === factor);
                      return (
                        <Badge key={factor} variant="secondary" className="px-3 py-1.5 bg-amber-100 text-amber-800 flex items-center gap-2">
                          <span>{option?.label || factor}</span>
                          <button 
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              risk_factors: prev.risk_factors.filter(f => f !== factor)
                            }))}
                            className="hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between border-amber-200"
                    >
                      {formData.risk_factors.length > 0 
                        ? `${formData.risk_factors.length} risk factor(s) selected`
                        : "Select risk factors..."
                      }
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <div className="max-h-60 overflow-auto">
                      {riskFactorOptions.length === 0 ? (
                        <div className="p-4 text-sm text-slate-500 text-center">
                          No risk factors available.
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {riskFactorOptions.map((option) => {
                            const isSelected = formData.risk_factors.includes(option.value);
                            return (
                              <div 
                                key={option.id || option.value}
                                className={`flex items-center space-x-3 p-2 rounded-md cursor-pointer hover:bg-slate-100 transition-colors ${isSelected ? 'bg-amber-50' : ''}`}
                                onClick={() => {
                                  if (isSelected) {
                                    setFormData(prev => ({
                                      ...prev,
                                      risk_factors: prev.risk_factors.filter(f => f !== option.value)
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      risk_factors: [...prev.risk_factors, option.value]
                                    }));
                                  }
                                }}
                              >
                                <Checkbox 
                                  checked={isSelected}
                                  className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                                />
                                <span className="flex-1 text-sm">{option.label}</span>
                                {isSelected && <Check className="w-4 h-4 text-amber-600" />}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-amber-700">Select all applicable hazards for this work</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tags">Tags (comma separated)</Label>
                <Input
                  id="tags"
                  placeholder="transformer, 69kV, major-project"
                  value={formData.tags}
                  onChange={(e) => handleChange('tags', e.target.value)}
                  data-testid="tags-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="safety_notes">Manual Safety Notes</Label>
                <Textarea
                  id="safety_notes"
                  placeholder="Arc flash hazard - Category 4 PPE required. Confined space entry required..."
                  value={formData.safety_notes}
                  onChange={(e) => handleChange('safety_notes', e.target.value)}
                  rows={3}
                  className="border-amber-300 focus:ring-amber-500"
                  data-testid="safety-notes-input"
                />
                <p className="text-xs text-slate-500">
                  Add any additional safety notes. AskStewie will auto-generate guidance based on your selections above.
                </p>
              </div>

              {/* AskStewie Preview */}
              {(formData.asset_type || formData.risk_factors.length > 0) && (
                <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-amber-600" />
                    <span className="font-semibold text-amber-800">AskStewie Safety Intelligence</span>
                  </div>
                  <p className="text-sm text-amber-700">
                    Safety guidance will be auto-generated for:
                    <br />
                    <strong>Asset:</strong> {assetTypeOptions.find(o => o.value === formData.asset_type)?.label || formData.asset_type || 'Not selected'}
                    <br />
                    <strong>Risks:</strong> {formData.risk_factors.map(rf => riskFactorOptions.find(o => o.value === rf)?.label || rf).join(', ') || 'None selected'}
                  </p>
                  <p className="text-xs text-amber-600 mt-2">
                    Full safety analysis will be available on the Work Order detail page after creation.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Equipment Request Options */}
          <Card className="border-purple-200 bg-gradient-to-br from-slate-50 to-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-800">
                <Truck className="w-5 h-5" />
                Equipment Request Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-slate-600">
                Request specialized equipment or logistics support. Notifications will be sent automatically.
              </p>
              
              {/* Star Team */}
              <div className="p-4 bg-white rounded-lg border border-purple-100 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-purple-600" />
                    <Label className="text-purple-800 font-semibold">Star Team Required?</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">No</span>
                    <Switch
                      checked={formData.star_team_required === 'yes'}
                      onCheckedChange={(checked) => handleChange('star_team_required', checked ? 'yes' : 'no')}
                    />
                    <span className="text-sm text-slate-500">Yes</span>
                  </div>
                </div>
                {formData.star_team_required === 'yes' && (
                  <div className="space-y-2 pt-2">
                    <Label htmlFor="star_team_notes">Star Team Notes</Label>
                    <Textarea
                      id="star_team_notes"
                      placeholder="Describe the specialized work requiring Star Team..."
                      value={formData.star_team_notes}
                      onChange={(e) => handleChange('star_team_notes', e.target.value)}
                      rows={2}
                      className="border-purple-200"
                    />
                    <p className="text-xs text-purple-600">
                      A logistics request will be created and dispatched to the Star Team coordinator.
                    </p>
                  </div>
                )}
              </div>

              {/* Heavy Hauling */}
              <div className="p-4 bg-white rounded-lg border border-purple-100 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-purple-600" />
                    <Label className="text-purple-800 font-semibold">Heavy Hauling Required?</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">No</span>
                    <Switch
                      checked={formData.heavy_hauling_required === 'yes'}
                      onCheckedChange={(checked) => handleChange('heavy_hauling_required', checked ? 'yes' : 'no')}
                    />
                    <span className="text-sm text-slate-500">Yes</span>
                  </div>
                </div>
                {formData.heavy_hauling_required === 'yes' && (
                  <div className="space-y-3 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="heavy_size">Load Size</Label>
                        <Input
                          id="heavy_size"
                          placeholder="e.g., 20ft x 8ft"
                          value={formData.heavy_hauling_details.size}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            heavy_hauling_details: { ...prev.heavy_hauling_details, size: e.target.value }
                          }))}
                          className="border-purple-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="heavy_weight">Estimated Weight</Label>
                        <Input
                          id="heavy_weight"
                          placeholder="e.g., 50,000 lbs"
                          value={formData.heavy_hauling_details.weight}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            heavy_hauling_details: { ...prev.heavy_hauling_details, weight: e.target.value }
                          }))}
                          className="border-purple-200"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="heavy_pickup">Pickup Location</Label>
                        <Input
                          id="heavy_pickup"
                          placeholder="Warehouse address or yard"
                          value={formData.heavy_hauling_details.pickup_location}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            heavy_hauling_details: { ...prev.heavy_hauling_details, pickup_location: e.target.value }
                          }))}
                          className="border-purple-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="heavy_dropoff">Dropoff Location</Label>
                        <Input
                          id="heavy_dropoff"
                          placeholder="Substation or job site"
                          value={formData.heavy_hauling_details.dropoff_location}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            heavy_hauling_details: { ...prev.heavy_hauling_details, dropoff_location: e.target.value }
                          }))}
                          className="border-purple-200"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-purple-600">
                      A heavy hauling logistics request will be created with these details.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Standard Equipment Requested */}
          <Card>
            <CardHeader>
              <CardTitle>Equipment Requested</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="equipment_requested">Equipment Type</Label>
                  <Select value={formData.equipment_requested} onValueChange={(v) => handleChange('equipment_requested', v)}>
                    <SelectTrigger data-testid="equipment-requested-select">
                      <SelectValue placeholder="Select equipment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gradall">Gradall</SelectItem>
                      <SelectItem value="man_lift">Man Lift</SelectItem>
                      <SelectItem value="back_hoe">Back Hoe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="order_placed">Order Placed</Label>
                  <Select value={formData.order_placed} onValueChange={(v) => handleChange('order_placed', v)}>
                    <SelectTrigger data-testid="order-placed-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tce_needed">Is TCE Needed?</Label>
                  <Select value={formData.tce_needed} onValueChange={(v) => handleChange('tce_needed', v)}>
                    <SelectTrigger data-testid="tce-needed-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Crew Size */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Crew Size
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600 mb-4">
                Specify the number of crew members needed by classification.
              </p>
              
              {/* Current Crew Configuration */}
              {Object.keys(formData.crew_size).length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800 mb-2">Current Crew Configuration:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(formData.crew_size).map(([classId, count]) => {
                      const classInfo = crewClasses.find(c => c.id === classId);
                      return (
                        <Badge key={classId} variant="secondary" className="px-3 py-1.5 bg-blue-100 text-blue-800 flex items-center gap-2">
                          <span>{classInfo?.label || classId}: {count}</span>
                          <button 
                            type="button"
                            onClick={() => {
                              const newCrewSize = { ...formData.crew_size };
                              delete newCrewSize[classId];
                              handleChange('crew_size', newCrewSize);
                            }}
                            className="hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Total: {Object.values(formData.crew_size).reduce((a, b) => a + b, 0)} crew members
                  </p>
                </div>
              )}

              {/* Add Crew Class */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="space-y-2">
                  <Label>Employee Class</Label>
                  <Select 
                    value="" 
                    onValueChange={(classId) => {
                      if (classId && !formData.crew_size[classId]) {
                        handleChange('crew_size', { ...formData.crew_size, [classId]: 1 });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="crew-class-select">
                      <SelectValue placeholder="Select class to add" />
                    </SelectTrigger>
                    <SelectContent>
                      {crewClasses.filter(c => !formData.crew_size[c.id]).map((crewClass) => (
                        <SelectItem key={crewClass.id} value={crewClass.id}>
                          {crewClass.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Crew Count Adjusters */}
              {Object.keys(formData.crew_size).length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                  {Object.entries(formData.crew_size).map(([classId, count]) => {
                    const classInfo = crewClasses.find(c => c.id === classId);
                    return (
                      <div key={classId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                        <span className="text-sm font-medium truncate mr-2">{classInfo?.label || classId}</span>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              const newCount = Math.max(0, count - 1);
                              if (newCount === 0) {
                                const newCrewSize = { ...formData.crew_size };
                                delete newCrewSize[classId];
                                handleChange('crew_size', newCrewSize);
                              } else {
                                handleChange('crew_size', { ...formData.crew_size, [classId]: newCount });
                              }
                            }}
                          >
                            -
                          </Button>
                          <span className="w-8 text-center font-bold">{count}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              handleChange('crew_size', { ...formData.crew_size, [classId]: count + 1 });
                            }}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/work-orders')}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#0056D2] hover:bg-[#0044A4]"
              disabled={loading}
              data-testid="submit-wo-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Creating...' : 'Create Work Order'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateWorkOrderPage;
